import { useTranslation } from "@sagot/i18n";
import { CodeBlock, Icon, Segmented } from "@sagot/ui";
import { useState } from "react";

const K = "deployKnowledge.widgetStudio.install";
/** Keys of `SnippetsOut.mobile` (backend `MobileFramework`), in the order they are offered. */
const FRAMEWORKS = ["react_native", "flutter", "ios", "android", "maui", "capacitor", "ios_launch", "android_launch"] as const;
type Framework = (typeof FRAMEWORKS)[number];
const NOTES = ["channel", "same", "updates", "guests"] as const;
const LAUNCH_NOTES = ["backend", "browser", "push"] as const;
const LAUNCH_FRAMEWORKS: readonly Framework[] = ["ios_launch", "android_launch"];

interface MobileGuideProps {
  /** `SnippetsOut.mobile`: one WebView example per framework. */
  examples: Readonly<Record<string, string>>;
}

/** Native apps: open the hosted chat page in the framework's WebView. Nothing to download from us. */
export function MobileGuide({ examples }: MobileGuideProps) {
  const { t } = useTranslation();
  const [framework, setFramework] = useState<Framework>("react_native");
  const label = t(`${K}.mobileFrameworks.${framework}`);
  return (
    <div className="space-y-4" data-testid="mobile-guide">
      <p className="text-xs text-fg-muted">{t(`${K}.mobileIntro`)}</p>
      <Segmented
        className="flex w-full flex-wrap"
        aria-label={t(`${K}.mobileFramework`)}
        value={framework}
        onValueChange={setFramework}
        options={FRAMEWORKS.map((value) => ({ value, label: t(`${K}.mobileFrameworks.${value}`) }))}
      />
      <CodeBlock code={examples[framework] ?? ""} label={t(`${K}.snippetLabel`, { method: label })} copyLabel={t("common.copy")} copiedLabel={t("common.copied")} />
      <section className="rounded-lg border border-line bg-surface-2 p-3.5" aria-labelledby="mobile-notes-title">
        <h3 id="mobile-notes-title" className="flex items-center gap-1.5 text-sm font-bold">
          <Icon name="info" className="size-4 text-primary-ink" />
          {t(`${K}.mobileNotes.title`)}
        </h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-fg-muted">
          {NOTES.map((note) => <li key={note}>{t(`${K}.mobileNotes.${note}`)}</li>)}
          {LAUNCH_FRAMEWORKS.includes(framework) && LAUNCH_NOTES.map((note) => <li key={note}>{t(`${K}.mobileNotes.launch.${note}`)}</li>)}
        </ul>
      </section>
    </div>
  );
}
